import type { HouseAccountReportView } from 'shared-models/browser';
import { formatMoney } from '../../../../../utils/money';

const pad = (value: number): string => String(value).padStart(2, '0');

export function currentMonthStatementRange(now: Date = new Date()): { from: string; to: string } {
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: `${from.getFullYear()}-${pad(from.getMonth() + 1)}-${pad(from.getDate())}`,
    to: `${to.getFullYear()}-${pad(to.getMonth() + 1)}-${pad(to.getDate())}`,
  };
}

export function formatStatementDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const entryDescription = (entry: HouseAccountReportView['entries'][number]): string => {
  if (entry.kind === 'charge') return entry.orderId == null ? 'חיוב בהקפה' : `חיוב הזמנה #${entry.orderId}`;
  if (entry.settlementMethod === 'cash') return 'תשלום חוב במזומן';
  return entry.settlementMethod === 'bank_transfer' ? 'תשלום חוב בהעברה בנקאית' : 'תשלום חוב באשראי';
};

export function buildHouseAccountStatementPrintHtml(report: HouseAccountReportView): string {
  const amount = (value: number): string => `${escapeHtml(formatMoney(value))} ₪`;
  const period = report.rangeFrom && report.rangeTo ? `${report.rangeFrom} - ${report.rangeTo}` : 'כל התקופות';
  const rows = report.entries.map((entry) => {
    const charge = entry.kind === 'charge' ? amount(entry.amount) : '';
    const settlement = entry.kind === 'settlement' ? amount(entry.amount) : '';
    return `<tr>
      <td>${escapeHtml(formatStatementDate(entry.createdAt))}</td>
      <td>${escapeHtml(entryDescription(entry))}</td>
      <td class="num">${charge}</td>
      <td class="num">${settlement}</td>
      <td class="num">${amount(entry.balance)}</td>
    </tr>`;
  }).join('');
  const title = `דוח חשבון - ${escapeHtml(report.customer.fullName)}`;
  const paidTotal = report.cashPaidAmount + report.creditPaidAmount + report.bankTransferPaidAmount;

  return `<!doctype html>
<html lang="he" dir="rtl">
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <style>
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #fff; color: #1f2933; font-family: 'Segoe UI', Arial, sans-serif; direction: rtl; }
      .host { padding: 16px; }
      h1 { margin: 0 0 6px; font-size: 20px; }
      .meta { margin: 0 0 16px; color: #4b5563; font-size: 13px; }
      .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
      .summary div { border: 1px solid #c7ccd1; padding: 8px; }
      .summary span { display: block; color: #6b7280; font-size: 11px; }
      .summary strong { display: block; margin-top: 3px; font-size: 14px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { border: 1px solid #c7ccd1; padding: 6px; text-align: right; }
      thead th, tfoot th { background: #eef1f4; }
      td.num, th.num { text-align: center; font-variant-numeric: tabular-nums; direction: ltr; }
      tbody tr:nth-child(even) td { background: #f7f8fa; }
      .empty { text-align: center; color: #6b7280; padding: 18px; }
      @page { size: A4; margin: 10mm; }
      @media print { .host { padding: 0; } thead { display: table-header-group; } tr { break-inside: avoid; } }
    </style>
  </head>
  <body>
    <div class="host">
      <h1>${title}</h1>
      <p class="meta">${escapeHtml(report.businessName)} | ${escapeHtml(report.customer.mobilePhone)} | תקופה: ${escapeHtml(period)}</p>
      <div class="summary">
        <div><span>יתרת פתיחה</span><strong>${amount(report.openingBalance)}</strong></div>
        <div><span>חיובים בתקופה</span><strong>${amount(report.chargedAmount)}</strong></div>
        <div><span>תשלומים בתקופה</span><strong>${amount(paidTotal)}</strong></div>
        <div><span>יתרת סגירה</span><strong>${amount(report.leftToPay)}</strong></div>
      </div>
      <table>
        <thead><tr><th>תאריך</th><th>פעולה</th><th class="num">חיוב</th><th class="num">תשלום</th><th class="num">יתרה</th></tr></thead>
        <tbody>${rows || '<tr><td class="empty" colspan="5">אין תנועות בתקופה שנבחרה</td></tr>'}</tbody>
        <tfoot><tr><th colspan="2">סה״כ</th><th class="num">${amount(report.chargedAmount)}</th><th class="num">${amount(paidTotal)}</th><th class="num">${amount(report.leftToPay)}</th></tr></tfoot>
      </table>
    </div>
  </body>
</html>`;
}

export function printHouseAccountStatement(report: HouseAccountReportView): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0', visibility: 'hidden' });
  document.body.appendChild(iframe);
  const cleanup = () => iframe.parentNode?.removeChild(iframe);
  iframe.addEventListener('load', () => {
    const frameWindow = iframe.contentWindow;
    if (!frameWindow) return cleanup();
    try {
      frameWindow.focus();
      frameWindow.print();
    } finally {
      setTimeout(cleanup, 500);
    }
  }, { once: true });
  const frameDocument = iframe.contentWindow?.document;
  if (!frameDocument) {
    cleanup();
    return;
  }
  frameDocument.open();
  frameDocument.write(buildHouseAccountStatementPrintHtml(report));
  frameDocument.close();
}